import { makeFunctionalNext, NoParams, Resolver } from '../definition/resolver';
import type { RejectUndefinedData } from '../../resources/types';
import { toError } from '../definition/resolverState';

export const LINK_ONLY_NO_FETCH_MESSAGE =
  'Link-only resource has no fetch; it is populated only via links. Requested data was not present in cache.';

/**
 * Create a resolver for a resource that has no transport of its own — its data
 * arrives only via links from another resource.
 *
 * Use it for a by-id resource when there is no by-id endpoint: a bulk query
 * (e.g. a list or date range) fans out into per-id entries through a `.push()`
 * / `.pull()` link, and those entries are read straight from the cache. See the
 * "Client-side normalization" recipe.
 *
 * Because there is nothing to fetch, any resolve or refresh emits an error
 * state carrying {@link LINK_ONLY_NO_FETCH_MESSAGE}. On a cold read that means
 * "not in cache yet"; consumers must treat it as such rather than a transport
 * failure. Refresh / invalidate / poll is always a caller mistake — there is
 * nothing to refetch — so any present data is forwarded as stale alongside the
 * error and reset on the next link push.
 *
 * Prefer the `defineLinkOnlyResource` shorthand, which wires this resolver for
 * you.
 *
 * @param name - Resolver identifier, used in debugging and devtools.
 *
 * @example
 * ```ts
 * const CalendarEvent = defineResource({
 *   typeName: 'calendar:CalendarEvent',
 *   resolver: fromLinksOnly<CalendarEventData, { id: string }>(),
 *   args: ['id'],
 * }).register();
 * ```
 */
export const fromLinksOnly = <TData, TParams = NoParams>(
  name = 'fromLinksOnly'
): RejectUndefinedData<TData, Resolver<TParams, TData>> =>
  ({
    name,
    tags: ['links-only'],
    attach: (__params, runtime) => {
      const next = makeFunctionalNext(runtime);

      return {
        refresh: () => {
          next(toError(new Error(LINK_ONLY_NO_FETCH_MESSAGE)));
        },
        detach: () => {},
      };
    },
  }) as RejectUndefinedData<TData, Resolver<TParams, TData>>;
